import cookie from 'js-cookie';
import { Component, Vue } from 'nuxt-property-decorator';
import { gql } from 'graphql-tag';
import { MODULE } from '../../config';
import { AdminUser, AdminUserStatus } from '../../schema';
import { EVENT } from '~/types/event';

/**
 * Login page
 *
 * @export
 * @class LoginPage
 * @extends {Vue}
 */
@Component({
  head: {
    title: 'Login'
  }
})
export default class Page extends Vue {
  loading = false;
  form = {
    data: {
      username: '',
      password: ''
    },
    type: 'username',
    rules: {
      username: [(v: string) => !!v || 'Username is required'],
      password: [
        (v: string) => !!v || 'Password is required',
        (v: string) =>
          v.length <= 25 ||
          'Please enter your password correctly (max 25 character)'
      ]
    },
    valid: false
  };

  async submit() {
    this.loading = true;

    try {
      const res = await this.$auth.loginWith('local', {
        data: this.form.data
      });
      const token = (res as any)?.data?.token;
      // Set token in cookie for api plugin to use
      cookie.set('access-token', token);
      // Fetch user profile after login and set to auth module
      const profile = await this.fetchProfile();
      this.$auth.setUser(profile);
      this.$auth.$storage.setUniversal('user', profile);
      // Emit login event to update navigation and other components
      this.$emitter.emit(EVENT.LOGIN, true);
    } catch (err) {
      const message = err?.response?.data?.toString();
      const text = this.$replaceGrapQLError(message || err);
      if (/password required/gi.test(text)) {
        this.form.type = 'password';
      } else if (/password not set/gi.test(text)) {
        this.form.type = 'set_password';
      } else {
        this.$emitter.emit('TOAST', text);
      }
    } finally {
      this.loading = false;
    }
  }

  get isWhitelabel(): boolean {
    return this.$acl([MODULE.AUTH, MODULE.AUTH_LOGIN].join('.'), 'whitelabel');
  }

  validationPassword(v: string) {
    const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    return (
      pattern.test(v) ||
      'Password must be at least 8 characters, including uppercase, lowercase, numbers, and special characters'
    );
  }

  async fetchProfile() {
    const query = gql`
      query User {
        Admin {
          User {
            profile {
              id
              type
              role
              status
              username
              access {
                id
                role
                modules
              }
            }
          }
        }
      }
    `;

    const { data } = await this.$api.query<{
      Admin: { User: { profile: AdminUser } };
    }>({ query });
    const profile = data?.Admin?.User?.profile;
    if (profile) {
      if (profile.status === AdminUserStatus.Active) return profile;
      throw new Error('Your account is not active');
    } else {
      throw new Error('Profile not found');
    }
  }
}
